import React, { useMemo } from "react";

import FollowPopover from "../components/FollowPopover";

import pdfImg from "@/assets/images/icons/pdf.svg";
import AvatarText from "@/components/reusables/AvatarText";
import OptionSelectorMemo from "@/components/reusables/OptionSelectors/OptionSelectorMemo";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { SingleDataEditor } from "@/components/reusables/data-table/single-data-editor";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { OptionTypeConstants } from "@/constants/constants";
import { SELECTOR_ID } from "@/constants/global-constants";
import { PR_CUSTOMER_TYPE } from "@/constants/project-register.constant";
import { TaskClaimsMessages } from "@/constants/task-claims.constant";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { TEstimationData } from "@/interfaces/project-register.interface";
import { openFilePreviewInNewTab } from "@/lib/file-image.utils";
import { cn, renderFullName } from "@/lib/utils";
import { useTaskClaimsSheetContext } from "@/pages/task-and-claims/context/TaskClaimsSheet";
import { useMutateClaimsEstimateMutation } from "@/redux/api/claimsQuery";
import { TAllOptions } from "@/schemas/GlobalSchema";
import {
  EstimationBaseSchema,
  TEstimationBaseSchema,
} from "@/schemas/project-register.schema";
import { customToast } from "@/utils/custom-toast";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";
import { validateResponse } from "@/utils/helper-functions/form.helper";

type dateKey = { ["status"]: string } | { ["submissionMethod"]: string };

type ColumnDependencies = {
  isEstimate: boolean;
  statusList: TAllOptions[];
  submissionList: TAllOptions[];
  setIsOption: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      statusFor: string;
    }>
  >;
  handleChangeStatus: ({
    leadsId,
    estimateId,
    data,
  }: {
    leadsId: string;
    estimateId: string;
    data: dateKey;
  }) => Promise<void>;

  getActionButton: (
    row: GlobalRowDataTypes<TEstimationData>,
  ) => ActionButtonDataTypes[];
  handleSetStatus: (
    id: string,
    estimateId: string,
    type: "final" | "archive" | "activate" | "final/undo",
  ) => Promise<void>;
  isLoading: boolean;
  updateParams: (key: string, value: string) => void;
  hasWritePermission: boolean;
  isClaims: boolean;
};

export const useEstimationColumns = ({
  isEstimate,
  statusList,
  submissionList,
  setIsOption,
  handleChangeStatus,
  handleSetStatus,
  isLoading,
  updateParams,
  hasWritePermission,
  getActionButton,
  isClaims,
}: ColumnDependencies): ExtendedColumnDef<TEstimationData>[] => {
  // const QuoteWrapper = isClaims ? SingleDataEditor : React.Fragment;
  const [mutate] = useMutateClaimsEstimateMutation();

  const { shouldDisableClaims } = useTaskClaimsSheetContext();

  const onSubmit = async (props: {
    data: Partial<TEstimationBaseSchema>;
    claimsId: string;
    estimateId: string;
    isDraft: boolean;
  }): Promise<void> => {
    const { data, claimsId, estimateId, isDraft } = props;

    const cleanedData = {
      ...data,
      ...(data.quoteVersion && { quoteName: data.quoteVersion.trim() }),
    };

    const response = await mutate({
      claimsId,
      estimateId,
      data: {
        ...cleanedData,
        isDraft,
      },
      method: "PUT",
    });

    validateResponse({
      response,
      onSuccess() {
        customToast.success(TaskClaimsMessages.QUOTE_VERSION_UPDATE);
      },
    });
  };

  const canMutate = useMemo(() => {
    return !hasWritePermission || !isClaims || shouldDisableClaims;
  }, [hasWritePermission, isClaims, shouldDisableClaims]);

  return useMemo(
    () => [
      {
        map: {
          key: "id",
          valueFrom: "id",
        },
        id: SELECTOR_ID,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
            className={cn(
              "hidden group-hover/header:block",
              table.getIsSomePageRowsSelected() && "block",
              table.getIsAllPageRowsSelected() && "!block",
            )}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),

        enableSorting: false,
        enableHiding: false,
      },
      {
        sortBy: "clientName",
        meta: {
          className: !isEstimate ? "hidden" : "",
          classNameRow: `${!isEstimate ? "hidden" : ""} truncate`,
          showRightBorder: true,
          minWidth: 300,
        },
        map: {
          excludeFromExport: !isEstimate,
          key: "Client",
          dataTransformer(data): string {
            const lead = data?.lead;

            return lead?.customer?.name || lead?.prospect?.name || "";
          },
        },
        enableSorting: true,
        accessorKey: "client",
        header: "Client",
        cell: ({ row }): React.JSX.Element => {
          const lead = row.original?.lead;
          const isProspect = lead?.type === PR_CUSTOMER_TYPE.PROSPECT;

          return (
            <AvatarText
              src={
                (isProspect ? lead?.prospect?.logo : lead?.customer?.logo) || ""
              }
              name={
                (isProspect
                  ? lead?.prospect?.shortName
                  : lead?.customer?.shortName) || ""
              }
              title={
                (isProspect ? lead?.prospect?.name : lead?.customer?.name) || ""
              }
              subtitle={lead?.type}
              classNames={{
                titleClassName: "max-w-[350px] truncate",
              }}
            />
          );
        },
      },
      {
        sortBy: "quoteVersion",
        meta: {
          showRightBorder: true,
          minWidth: 250,
        },
        map: {
          key: "Quote Version",
          valueFrom: "quoteVersion",
        },
        accessorKey: "quote Version",
        header: () => "Quote Version",
        cell: ({ row }): React.JSX.Element => {
          return (
            <SingleDataEditor
              schema={EstimationBaseSchema.partial()}
              name='quoteVersion'
              type='text'
              data={row.original.quoteVersion ?? "-"}
              handleSubmit={(data) => {
                void onSubmit({
                  data,
                  claimsId: row.original.claimsId ?? "-",
                  estimateId: row.original.id,
                  isDraft: row.original?.isDraft,
                });
              }}
              config={{
                isTable: true,
                editPosition: "right",
                showEdit: !canMutate,
              }}
            >
              <div className='capitalize '>
                <h3>{row.original.quoteVersion}</h3>
              </div>
            </SingleDataEditor>
          );
        },
      },
      {
        sortBy: "quoteFile",
        meta: {
          showRightBorder: true,
          minWidth: 250,
        },
        map: {
          key: "Quote File",
          valueFrom: "quoteFile",
        },
        accessorKey: "quote File",
        header: () => (
          <div className='text-left whitespace-nowrap w-[150px]'>
            Quote File
          </div>
        ),
        cell: ({ row }): React.JSX.Element => {
          return (
            <div className='flex items-center whitespace-normal'>
              <img src={pdfImg} alt='pdf' className='w-8 mr-1' />
              <p
                onClick={() => openFilePreviewInNewTab(row.original?.quoteFile)}
                className='break-normal cursor-pointer hover:text-primary hover:underline'
              >
                {row.original?.quoteVersion}
              </p>
            </div>
          );
        },
      },
      {
        sortBy: "amount",
        meta: {
          showRightBorder: true,
          minWidth: 150,
        },
        map: {
          key: "Amount",
          dataTransformer(data): string {
            return `$ ${data?.amount / 100}`;
          },
        },
        accessorKey: "amount",
        header: () => <div className='text-left whitespace-nowrap'>Amount</div>,
        cell: ({ row }): React.JSX.Element => (
          <h3 className='text-aldgray whitespace-nowrap dark:text-white '>
            $ {row.original?.amount / 100}
          </h3>
        ),
      },
      {
        sortBy: "statusOptionId",
        meta: {
          showRightBorder: true,
        },
        map: {
          key: "Status",
          dataTransformer(data): string {
            return data?.status?.name || "-";
          },
        },
        accessorKey: "status",
        header: "Status",
        cell: ({ row }): React.JSX.Element => (
          <OptionSelectorMemo
            disabled={canMutate}
            activeId={row.original?.status?.id ?? ""}
            optionList={statusList ?? []}
            setIsOption={setIsOption}
            isOptionAdditional={() => {
              updateParams("estimateId", row.original.id);
            }}
            onValueChange={async (value) => {
              await handleChangeStatus({
                leadsId: row.original.leadsId ?? "leadsId",
                estimateId: row.original?.id || "",
                data: { status: value as string },
              });
            }}
            statusFor={OptionTypeConstants.ESTIMATION_STATUS}
            key={row.original.id}
            config={{
              isRequired: false,
              isFinal: row.original.isFinal,
            }}
          />
        ),
      },

      {
        sortBy: "createdBy",
        meta: {
          showRightBorder: true,
          minWidth: 250,
        },
        map: {
          key: "Created By",
          dataTransformer(data): string {
            return `${data?.createdBy?.firstName} ${data?.createdBy?.lastName}`;
          },
        },
        accessorKey: "created By",
        header: () => (
          <div className='text-left whitespace-nowrap'>Created by</div>
        ),
        cell: ({ row }): React.JSX.Element => {
          const name = renderFullName({
            params: row.original?.createdBy,
          });

          return (
            <AvatarText
              src={row.original?.createdBy?.firstName || ""}
              name={name}
              title={name}
            />
          );
        },
      },
      {
        sortBy: "isFinal",
        meta: {
          showRightBorder: true,
        },
        map: {
          key: "Final Quote",
          dataTransformer(data): string {
            return `${data?.isFinal ? "Final Estimate" : "Not Final"}`;
          },
        },
        accessorKey: "final Quote",
        header: () => (
          <div className='text-left whitespace-nowrap'>Final Quote</div>
        ),
        cell: ({ row }): React.JSX.Element => (
          <Button
            variant={row.original?.isFinal ? "default" : "outline"}
            className={cn(
              !row.original?.isFinal && "border-primary text-primary",
            )}
            disabled={
              isLoading ||
              !hasWritePermission ||
              !isClaims ||
              shouldDisableClaims
            }
            onClick={() =>
              handleSetStatus(
                row.original?.leadsId ?? "leadsId",
                row?.original?.id,
                !row.original.isFinal ? "final" : "final/undo",
              )
            }
          >
            {row.original.isFinal ? "Final Estimate" : "Set as Final"}
          </Button>
        ),
      },
      {
        sortBy: "submittedDate",
        meta: {
          showRightBorder: true,
        },
        map: {
          key: "Submitted On",
          dataTransformer(data): string {
            return dateFormat(data?.submittedDate, "dd/MM/yyyy");
          },
        },
        accessorKey: "submitted On",
        header: () => (
          <div className='text-left whitespace-nowrap'>Submitted On</div>
        ),
        cell: ({ row }) => dateFormat(row.original.submittedDate, "dd/MM/yyyy"),
      },
      {
        sortBy: "submissionOptionId",
        meta: {
          showRightBorder: true,
        },
        map: {
          key: "Submission method",
          dataTransformer(data): string {
            return data?.submissionMethod?.name ?? "-";
          },
        },
        accessorKey: "submission Method",
        header: () => (
          <div className='text-left whitespace-nowrap'>Submitted Method</div>
        ),
        cell: ({ row }) => (
          <OptionSelectorMemo
            disabled={canMutate}
            activeId={row.original?.submissionMethod?.id ?? ""}
            optionList={submissionList ?? []}
            setIsOption={setIsOption}
            onValueChange={async (value) => {
              await handleChangeStatus({
                leadsId: row.original.leadsId,
                estimateId: row.original?.id || "",
                data: { submissionMethod: value as string },
              });
            }}
            statusFor={OptionTypeConstants.ESTIMATION_SUBMISSION_METHOD}
            key={row.original.id}
            config={{
              isRequired: false,
              isFinal: row.original.isFinal,
            }}
          />
        ),
      },
      {
        sortBy: "followUpRequired",
        meta: {
          showRightBorder: true,
        },
        map: {
          key: "Follow Up",
          dataTransformer(data): string {
            return data?.followUpRequired ? "YES" : "NO";
          },
        },
        accessorKey: "follow Up",
        header: () => (
          <div className='text-left whitespace-nowrap'>Follow Up</div>
        ),
        cell: ({ row }): React.JSX.Element => {
          return (
            <div className={cn(!isClaims && "pointer-events-none")}>
              <FollowPopover
                leadsId={row.original.leadsId}
                estimateId={row.original.id}
                followUpDetails={row.original.followUpDetails}
                followUpRequired={row.original.followUpRequired}
                isFinal={row.original.isFinal}
                disabled={!hasWritePermission}
              />
            </div>
          );
        },
      },
      ...(isClaims
        ? [
            {
              id: "actions",
              header: "Actions",
              hideColumn: !hasWritePermission,
              cell: ({ row }: { row: GlobalRowDataTypes<TEstimationData> }) => (
                <CustomDropdown data={getActionButton(row)} />
              ),
            },
          ]
        : []),
    ],
    [statusList, submissionList, shouldDisableClaims],
  );
};
